import { useCallback, useEffect, useState } from 'react'

/**
 * localStorage key for the board leftnav expanded/collapsed state.
 *
 * Scoped to the board surface (FS-513): the V2 board renders the
 * {@link AppSidebar} as a slide-in overlay that starts collapsed, and the
 * top-left toggle expands it in place instead of navigating away. Persisting
 * the choice means reopening a board remembers whether you last had the rail
 * open. Other V2 screens (DataCatalog, Settings, KnowledgeGraph, …) mount the
 * sidebar permanently and do not read this key.
 */
export const SIDEBAR_EXPANDED_STORAGE_KEY = 'board:sidebar-expanded'

/**
 * Board leftnav expanded-state hook. Behaves like `useState<boolean>` but
 * persists to `localStorage` under {@link SIDEBAR_EXPANDED_STORAGE_KEY} so the
 * choice survives reloads and navigation between boards.
 *
 * Defaults to collapsed (`false`) — the board opens full-bleed and the user
 * reveals the rail via the top-left toggle, matching today's entry behavior
 * before FS-513.
 *
 * SSR / private-browsing safe: a missing `window`, an unparseable value, or a
 * throwing `localStorage` all fall back to {@link defaultExpanded} without
 * surfacing an error.
 *
 * @param defaultExpanded value used when nothing valid is stored (default `false`)
 * @returns `[expanded, setExpanded, toggle]`
 */
export function useSidebarExpanded(
  defaultExpanded = false,
): [boolean, (value: boolean | ((prev: boolean) => boolean)) => void, () => void] {
  const getInitialValue = (): boolean => {
    if (typeof window === 'undefined') {
      return defaultExpanded
    }

    try {
      const raw = window.localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY)
      if (raw === null) {
        return defaultExpanded
      }

      const parsed: unknown = JSON.parse(raw)
      return typeof parsed === 'boolean' ? parsed : defaultExpanded
    } catch {
      // JSON parse error or sessionStorage/localStorage access denied.
      return defaultExpanded
    }
  }

  const [expanded, setExpanded] = useState<boolean>(getInitialValue)

  // Persist outside the state updater. React Strict Mode double-invokes state
  // updaters in dev to surface impure ones, so a `localStorage.setItem` there
  // would fire twice; an effect keyed on `expanded` keeps the updater pure and
  // writes exactly once per committed value (idempotent on the mount write).
  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_EXPANDED_STORAGE_KEY,
        JSON.stringify(expanded),
      )
    } catch {
      // Silently ignore when storage is unavailable (private browsing).
    }
  }, [expanded])

  const toggle = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [])

  return [expanded, setExpanded, toggle]
}

export default useSidebarExpanded
